import React from 'react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { formatFeatureKey } from '../utils/smePlans';

const LIMIT_LABELS = {
  scope3Categories: 'Scope 3 categories',
  users: 'Users',
  sites: 'Sites',
  invoiceEntriesPerYear: 'Invoice entries / year',
  yearOnYearYears: 'Year-on-year history',
};

const titleCase = (value) => {
  if (!value) return 'Legacy / unmapped';
  return String(value)
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

/**
 * Read-only plan and resolved-entitlements summary.
 *
 * @param {{ organisation?: object, className?: string }} props
 * @returns {React.ReactElement}
 */
const PlanEntitlementsSummary = ({ organisation, className = '' }) => {
  const entitlements = organisation?.entitlements || {};
  const featureKeys = entitlements.featureKeys || entitlements.features || [];
  const limits = entitlements.limits || {};

  return (
    <Card className={`border-border bg-card text-card-foreground ${className}`}>
      <CardHeader>
        <CardTitle className='flex flex-wrap items-center gap-2 text-base'>
          Plan & entitlements
          <Badge variant='secondary'>{titleCase(organisation?.planId)}</Badge>
          {organisation?.subscriptionStatus && <Badge variant='outline'>{titleCase(organisation.subscriptionStatus)}</Badge>}
        </CardTitle>
        <CardDescription>
          Resolved access source: {titleCase(entitlements.source || entitlements.mode || 'legacy')}
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        {featureKeys.length > 0 ? (
          <div>
            <h4 className='mb-2 text-sm font-semibold text-foreground'>Included features</h4>
            <div className='flex flex-wrap gap-2'>
              {featureKeys.map((key) => (
                <Badge key={key} variant='outline'>
                  {formatFeatureKey(key)}
                </Badge>
              ))}
            </div>
          </div>
        ) : (
          <p className='text-sm text-muted-foreground'>No resolved entitlement catalog is available for this organisation.</p>
        )}

        {Object.keys(limits).length > 0 && (
          <div>
            <h4 className='mb-2 text-sm font-semibold text-foreground'>Plan limits</h4>
            <dl className='grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-3'>
              {Object.entries(limits).map(([key, value]) => (
                <div key={key} className='rounded-lg border border-border bg-muted/30 p-3'>
                  <dt className='text-muted-foreground'>{LIMIT_LABELS[key] || titleCase(key)}</dt>
                  <dd className='font-semibold text-foreground'>
                    {value == null ? 'Unlimited' : Number(value).toLocaleString('en-GB')}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <div className='flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground'>
          {entitlements.pdfBranding && <span>PDF branding: {titleCase(entitlements.pdfBranding)}</span>}
          {entitlements.aiPlanId && <span>AI plan: {titleCase(entitlements.aiPlanId)}</span>}
        </div>
      </CardContent>
    </Card>
  );
};

export default PlanEntitlementsSummary;
